// External
import { Injectable } from '@angular/core';
import moment from 'moment';

// Internal
import { LocalizedDatePipe } from '../../../../common/pipes/pipes';
import { DevicesService } from '../../process/devices/devices.service';
import { NotificationsService } from '../../process/notifications/notifications.service';
import { ValuesService } from '../../../../common/values/values.service';
import { ProductsConfigService } from '../../../../common/config/products.config.service';
import { AdobeDataLayerService } from '../../core/adobe.datalayer.service';
import { DevicesValuesService } from '../../../../common/values/devices.values';
import { IDevicesDetailsService } from '../idevices-details/idevices-details.service';
import { NotificationsSubtypes, NotificationsTypes, NotificationsFunctions, EventDescriptors, EventConditions, EventPalceholders } from '../../global/events/events.model';
import { EventsService } from '../../global/events/events.service';
import { UiIcon } from 'src/app/common/components/ui/ui-icon/ui-icon.models';

interface RewardNotificaitonPalceholders {
    reward: number;
    friends: number;
}

enum ThreatType {
    FILE = 'file',
    APP = 'app',
    URL = 'url',
    PROCESS = 'process'
}

const NotificationPropertyFragment = 'notification_';
const NotificationDataProperty = 'data';

@Injectable({
    providedIn: 'root'
})


export class INotificationsService {

    private malwareVsNoMalwareTips = {
        'notifications.network_attack.ip_reputation_incoming.tips': 'notifications.network_attack.ip_reputation_outgoing.noMalware.tips',
        'notifications.network_attack.ip_reputation_outgoing.tips': 'notifications.network_attack.ip_reputation_outgoing.noMalware.tips'
    };

    constructor(
        private readonly localizedDatePipe: LocalizedDatePipe,
        private readonly devicesService: DevicesService,
        private readonly notificationsService: NotificationsService,
        private readonly valuesService: ValuesService,
        private readonly productsConfigService: ProductsConfigService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly devicesValuesService: DevicesValuesService,
        private readonly iDevicesDetailsService: IDevicesDetailsService,
        private readonly eventsService: EventsService
    ) { }

    /**
     * Adds the notification default subtype to the notification object if notification does not have a subtype
     * @param notification The notification object
     */
    private addNotificationDefaultSubtypeToNotification(notification): void {
        const subtype = notification?.app_fields?.notification_params?.notification_subtype;
        if (!subtype) {
            notification.app_fields.notification_params.notification_subtype = NotificationsSubtypes.DEFAULT;
        }
    }

    /**
     * Adds the notification.app_fields.notificaiton_params tot the notificaiton interface
     * @param notification The notification object
     * @param notificationInterface The notification interface
     */
    private addNotificationParamsToNotificationInterface(notification, notificationInterface): void {
        const notificationParams = notification?.app_fields?.notification_params ?? {};

        for (const param in notificationParams) {
            let modifiedParam = '';
            if (param === NotificationDataProperty) {
                continue;
            }
            if (param.indexOf(NotificationPropertyFragment) === 0) {
                modifiedParam = param.split(NotificationPropertyFragment)[1];
            } else {
                modifiedParam = param;
            }

            if (notificationParams[param] !== '' && notificationParams[param] !== undefined) {
                notificationInterface[modifiedParam] = notificationParams[param];
            }
        }
    }

    /**
     * Adds the notification.app_fields.notificaiton_params.data tot the notificaiton interface
     * @param notification The notification object
     * @param notificationInterface The notification interface
     */
    private addNotificationDataParamsToNotificationInterface(notification, notificationInterface): void {
        const notificationParamsData = notification?.app_fields?.notification_params?.data ?? {};
        for (const param in notificationParamsData) {
            let modifiedParam = '';
            if (param.indexOf(NotificationPropertyFragment) === 0) {
                modifiedParam = param.split(NotificationPropertyFragment)[1];
            } else {
                modifiedParam = param;
            }

            if (notificationParamsData[param] !== '' && notificationParamsData[param] !== undefined) {
                notificationInterface[modifiedParam] = notificationParamsData[param];
            }
        }
    }

    /**
     * Adds the notificationInterface.params  tot the notificaiton interface
     * @param notificationInterface The notification interface
     */
    private addNotificationInterfaceParamsToInterface(notificationInterface): void {
        const dataParams = notificationInterface?.params ?? {};
        for (const dataParam in dataParams) {
            if (dataParams[dataParam] !== '' && dataParams[dataParam] !== undefined) {
                notificationInterface[dataParam] = dataParams[dataParam];
            }
        }
    }

    /**
     * Computes and adds device_id and box_device_id to the notification interface
     * @param notification The notification object
     * @param notificationInterface The notification interface
     */
    private addNotificationInterfaceDeviceAndBoxId(notification, notificationInterface): void {
        const notificationDeviceId = notificationInterface?.device_id
                                    || notification?.app_fields?.notification_params?.device_id
                                    || notification?.app_fields?.connect_source?.device_id;
        if (!notificationInterface.device_id && notificationDeviceId) {
            notificationInterface.device_id = notificationDeviceId;
        }

        const device = this.devicesService.retrieveDeviceById(notificationDeviceId);
        const boxDeviceId = notificationInterface?.box_device_id;
        if ((device?.device_type === this.devicesValuesService.deviceType.ROUTER
                || device?.device_type === this.devicesValuesService.deviceType.NETWORK_EQUIPMENT)
            && !boxDeviceId) {
            notificationInterface.box_device_id = notificationDeviceId;
        }
    }

    /**
     * Adds the accessed_from to the notification interface
     * @param notificationInterface The notification interface
     */
    private addNotificationInterfaceAccessedFromApp(notificationInterface): void {
        const appName = notificationInterface?.apps?.[0]?.pretty_name;
        if (appName) {
            notificationInterface[EventDescriptors.ACCESSED_FROM] = appName;
        }
    }

    /**
     * Computes the formated threat details object
     * @param threatDetails The threat details object
     * @returns {object} The formated threat details object
     */
    private computeFormatedThreatDetails(threatDetails): object {
        const threat: any = {};
        threat[EventDescriptors.THREAT_NAME] = threatDetails?.[EventDescriptors.THREAT_NAME] ?? threatDetails?.threat;
        threat[EventConditions.THREAT_TYPE] = threatDetails[EventConditions.THREAT_TYPE];
        threat[EventPalceholders.THREAT_NAME] = threat[EventDescriptors.THREAT_NAME];
        threat[EventPalceholders.THREAT_TYPE] = threat[EventConditions.THREAT_TYPE];

        const objectType = threatDetails?.object_type;
        const objectInstance = threatDetails?.object;

        if (objectType === ThreatType.URL && typeof objectInstance === 'string') {
            threat[EventDescriptors.BLOCKED_URL] = objectInstance;
            threat[EventPalceholders.BLOCKED_URL] = objectInstance;
            threat[EventDescriptors.FILE_NAME] = objectInstance;
            threat[EventPalceholders.FILENAME] = objectInstance;
        } else if (objectType === ThreatType.PROCESS) {
            threat[EventDescriptors.BLOCKED_PROCESS] = objectInstance;
            threat[EventPalceholders.BLOCKED_PROCESS] = objectInstance;
        } else if (objectType === ThreatType.APP || objectType === ThreatType.FILE) {
            threat[EventDescriptors.FILE_NAME] = objectInstance;
            threat[EventPalceholders.FILENAME] = objectInstance;
        } else {
            for (const property in objectInstance) {
                threat[property] = objectInstance[property];
            }
        }
        return threat;
    }

    /**
     * Adds the single threat info to the notification interface.
     * @param notificationInterface The notification interface
     * @param threat The threat object
     */
    private addThreatInfoForASingleThreat(notificationInterface, threat): void {
        for (const property in threat) {
            notificationInterface[property] = threat[property];
        }

        if (notificationInterface?.[EventDescriptors.THREAT_NAME]) {
            notificationInterface[EventDescriptors.PROTOCOL] = this.computeProtocolForNotificationInterface(notificationInterface?.[EventDescriptors.THREAT_NAME]);
        }
    }

    /**
     * Computes the protocol for the notification interface based on the threat name
     * @param threatName The threat name
     * @returns {string} The protocol
     */
    private computeProtocolForNotificationInterface(threatName): string {
        if (!threatName) {
            return '';
        }
        const threatNameSplit = threatName.split('.');
        const protocol = threatNameSplit?.[threatNameSplit?.length - 1] ?? '';
        return protocol.toUpperCase();
    }

    /**
     * Adds the threat info to the notification interface.
     * If there is one single threat it adds the threat info to the notification interface.
     * Otherwise it adds the threats array to the notification interface
     * @param notificationInterface The notification interface
     */
    private addNotificationInterfaceThreatsInfo(notificationInterface): void {
        const threatGroupId = notificationInterface?.threat_group_id;
        const threatsCount = notificationInterface?.threats_count;
        const threatsDetails = notificationInterface?.threat_details;

        if (threatGroupId && threatsCount && threatsDetails) {
            const threats = [];
            const threatDetails = notificationInterface?.threat_details ?? [];

            for (const threatDetail of threatDetails) {
                threats.push(this.computeFormatedThreatDetails(threatDetail));
            }

            if (threats.length === 1) {
                this.addThreatInfoForASingleThreat(notificationInterface, threats[0]);
            } else {
                notificationInterface.threats = threats;
            }
        }
    }

    /**
     * Adds device info and/or box info to the notification interface
     * @param notification The notification object
     * @param notificationInterface The notification interface
     */
    public addNotificationInterfaceDeviceAndBoxInfo(notification, notificationInterface): void {
        const notificationDeviceId = notificationInterface.device_id;
        const boxDeviceId = notificationInterface?.box_device_id;
        const device = this.devicesService.retrieveDeviceById(notificationDeviceId);
        const box = this.devicesService.retrieveDeviceById(boxDeviceId);

        const notificationType = notification?.app_fields?.notification_params?.notification_type;
        const skipDeviceVerification = this.valuesService.notificationsSkipDeviceVerification.has(notificationType);
        notificationInterface.skipDeviceVerification = skipDeviceVerification;

        notificationInterface.device_icon = device?.device_icon;
        notificationInterface.device_os = device?.device_os;
        notificationInterface[EventDescriptors.DEVICE_NAME] = device?.display_name ?? notificationInterface?.device_name ?? notificationInterface?.display_name;

        if (!device && !skipDeviceVerification) {
            notificationInterface.device_deleted = true;
        }

        if (box) {
            notificationInterface.box_name = notificationInterface.box_name ?? box.display_name;
        }
    }

    /**
     * Adds the profile info to the notification interface
     * @param notificationInterface The notification interface
     */
    public addNotificationInterfaceProfileInfo(notificationInterface): void {
        const deviceId = notificationInterface.device_id;
        const device = this.devicesService.retrieveDeviceById(deviceId);

        notificationInterface.user_name = device?.processed?.device_profile?.profile_name;
        notificationInterface.user_avatar = device?.processed?.device_profile?.profile_pic;

    }

    /**
     * Tells if the notification has a box update cta
     * @param notificationInterface The notification interface
     * @param type The notification type
     * @param subtype The notification subtype
     * @returns {boolean} True if the notification has a box update cta
     */
    private notificationAdvisesUpgradingBoxVersion(notificationInterface, type, subtype): boolean {
        return type === NotificationsTypes.BOXSE_UPDATE
            && subtype === NotificationsSubtypes.NEW_UPDATE_AVAILABLE
            && notificationInterface.hasOwnProperty(EventDescriptors.VERSION)
            && notificationInterface.hasOwnProperty('latest_version')
            && notificationInterface?.[EventDescriptors.VERSION] !== notificationInterface?.latest_version;
    }

    /**
     * Tells if the notification has a malware scan cta
     * @param type The notification type
     * @param subtype The notification subtype
     * @returns {boolean} True if the notification has a malware scan cta
     */
    private notificationAdvisesPerformingMalwareScan(type, subtype): boolean {
        return type === NotificationsTypes.NETWORK_ATTACK && subtype === NotificationsSubtypes.DOS_OUTGOING;
    }

    /**
     * Computes the main button disabled state
     * @param notificationInterface The notification interface
     * @param type The notification type
     * @param subtype The notification subtype
     * @returns {boolean} True is the button should be disabled
     */
    private getMainButtonDisabledState(notificationInterface, type, subtype): boolean {
        if (notificationInterface?.skipDeviceVerification) {
            return false;
        }

        if (notificationInterface.device_deleted) {
            return true;
        }

        if (this.notificationAdvisesUpgradingBoxVersion(notificationInterface, type, subtype)) {
            return !!this.devicesService.getDeviceAutomaticUpgrade(notificationInterface?.device_id);
        }

        if (this.notificationAdvisesPerformingMalwareScan(type, subtype)) {
            const deviceId = notificationInterface?.device_id;
            this.iDevicesDetailsService.createQuickActionsInterface(deviceId);
            const device = this.devicesService.retrieveDeviceById(deviceId);
            const hasMalwareQuickAction = !!device?.interface?.quickActions?.malwareScan;
            return !hasMalwareQuickAction;
        }
        return false;
    }

    /**
     * Obtains the secondary button disabled state
     * @param notificationInterface The notification interface
     * @returns {boolean} True is the secondary button should be disabled
     */
    private getSecondaryButtonDisabledState(notificationInterface): boolean {
        return !this.devicesService.retrieveDeviceById(notificationInterface?.device_id);
    }

    /**
     * Obtains the notification subtype
     * @param notification The notification
     * @returns {string} The notification subtype
     */
    private getNotificationSubtype(notification): string {
        let subtype = notification?.app_fields?.notification_params?.notification_subtype;
        const type = notification?.app_fields?.notification_params?.notification_type;
        const app = notification?.app_fields?.connect_source?.app_id;

        if (type === subtype && type === NotificationsTypes.MALICIOUS_HOST && app !== this.valuesService.appBOX2) {
            subtype = NotificationsSubtypes.MALICIOUS_HOST_BMS;
        }
        return subtype;
    }

    /**
     * Changes the notification interface main link info when there is an exception for the notificaiton
     * @param notificationInterface The notification interface
     */
    public changeNotificationInterfaceMainLinkInfoForException(notificationInterface): void {
        notificationInterface.main_link_desc = 'notifications.main.link.exception.added';
        notificationInterface.main_link = this.valuesService.notificationsKeys.viewExceptionBtn;
        notificationInterface.main_link_funct = NotificationsFunctions.VIEW_EXCEPTIONS;
    }

    /**
     * Adds the protocol to the notification interface
     * @param notificationInterface The notification interface
     */
    private addNotificationInterfaceProtocol(notificationInterface): void {
        if (!notificationInterface[EventDescriptors.PROTOCOL]) {
            notificationInterface[EventDescriptors.PROTOCOL] = this.computeProtocolForNotificationInterface(notificationInterface?.[EventDescriptors.THREAT_NAME]);
        }
    }

    /**
     * Computes the placeholder for reward notificaiton, the reward and freinds number
     * @param notificationInterface The notification interface
     * @returns {RewardNotificaitonPalceholders} The reward placeholder object
     */
    private computeRewardPlaceholders(notificationInterface): RewardNotificaitonPalceholders {
        const maxUsages = notificationInterface?.max_usages ?? 0;
        const actualUsages = notificationInterface?.actual_usages ?? 0;
        const reward = notificationInterface?.reward ?? 0;
        let rewardPlaceholder = 0;
        if (maxUsages && actualUsages) {
            rewardPlaceholder = Math.ceil(moment.duration(reward, 'second').asMonths());
        } else {
            rewardPlaceholder = Math.ceil(moment.duration(reward, 'second').asDays());
        }

        return {
            reward: rewardPlaceholder,
            friends: maxUsages - actualUsages
        };
    }

    /**
     * Computes the timestamp placeholder
     * @param notification The notification object
     * @returns {string} The timestamp placeholder
     */
    private computeTimestampPlaceholder(notification): string {
        let timestampPlaceholder = '';
        if (notification?.app_fields?.timestamp) {
            timestampPlaceholder = this.localizedDatePipe.transform(notification.app_fields.timestamp, 'hourMinute').toString()
                        .concat(', ', this.localizedDatePipe.transform(notification.app_fields.timestamp, 'mediumDate').toString());
        }
        return timestampPlaceholder;
    }

    /**
     * This function is useful for the case when the placeholder is null or undefined.
     * All fields with these values are eliminated during computations but there are still cases where you need to display empty string
     * because the notification texts are not properly broken into smaller pieces and it will appear {{placeholder}}
     * in interface if 'placeholder' is null or undefined. In this case we want to display an empty string.
     * @param placeholder The placeholder value
     * @returns The placeholder value or empty string
     */
    private prepareFinalPalceholderForDisplay(placeholder): any|string {
        return placeholder ?? '';
    }

    /**
     * Adds the placeholders to the notification interface
     * @param notification The notification object
     * @param notificationInterface The notification interface to add the placeholders to
     */
    private addNotificationPlaceholdersToInterface(notification, notificationInterface): void {
        const rewardPalceholders = this.computeRewardPlaceholders(notificationInterface);

        notificationInterface.placeholders = {
            [EventPalceholders.PARENTAL_PRODUCT_NAME]: this.productsConfigService.getProductName(this.valuesService.productNamePA),
            [EventPalceholders.TS_PRODUCT_NAME]: this.productsConfigService.getProductName(this.valuesService.productNameTS),
            [EventPalceholders.MANUFACTURER_NAME]: this.productsConfigService.getManufacturerName(),
            [EventPalceholders.SOURCE_APPLICATION_NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.ACCESSED_FROM]),
            [EventPalceholders.APPLICATION_NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface.app_name),
            [EventPalceholders.ATTACK_SOURCE]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.ATTACK_SOURCE]),
            [EventPalceholders.BLOCKED_APPLICATION_NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface.blocked_app_name),
            [EventPalceholders.BLOCKED_APPLICATION]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.BLOCKED_APP]),
            [EventPalceholders.BLOCKED_FILE]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.BLOCKED_FILE]),
            [EventPalceholders.BLOCKED_INTEREST_NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface.blocked_interest_name),
            [EventPalceholders.BLOCKED_IP]: this.prepareFinalPalceholderForDisplay(notificationInterface.blocked_ip),
            [EventPalceholders.BLOCKED_PROCESS]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.BLOCKED_PROCESS]),
            [EventPalceholders.BLOCKED_URL]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.BLOCKED_URL]),
            [EventPalceholders.BOX_NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface?.box_name),
            [EventPalceholders.CHILD_NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface.child_name),
            [EventPalceholders.CLEAN_URL]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.CLEAN_URL]),
            [EventPalceholders.DEVICE_NAME]: notificationInterface[EventDescriptors.DISPLAY_NAME] ?? notificationInterface?.[EventDescriptors.DEVICE_NAME],
            [EventPalceholders.DISPLAY_NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.DISPLAY_NAME]),
            [EventPalceholders.DOMAIN]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.DOMAIN]),
            [EventPalceholders.EXTERNAL_IP]: this.prepareFinalPalceholderForDisplay(notificationInterface.external_ip),
            [EventPalceholders.EXTERNAL_PORT]: this.prepareFinalPalceholderForDisplay(notificationInterface.external_port),
            [EventPalceholders.FILENAME]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.FILE_NAME]),
            [EventPalceholders.HOST]: this.prepareFinalPalceholderForDisplay(notificationInterface.host),
            [EventPalceholders.IP]: this.prepareFinalPalceholderForDisplay(notificationInterface.ip),
            [EventPalceholders.LATEST_VERSION]: this.prepareFinalPalceholderForDisplay(notificationInterface.latest_version),
            [EventPalceholders.LOCAL_PORT]: this.prepareFinalPalceholderForDisplay(notificationInterface.local_port),
            [EventPalceholders.LOCATION]: this.prepareFinalPalceholderForDisplay(notificationInterface.location),
            [EventPalceholders.MALWARE]: this.prepareFinalPalceholderForDisplay(notificationInterface.malware),
            [EventPalceholders.NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.NAME]),
            [EventPalceholders.NUMBER_FRIENDS]: rewardPalceholders.friends,
            [EventPalceholders.NUMBER_DEVICES]: notificationInterface.no_devices,
            [EventPalceholders.PORT]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.PORT]),
            [EventPalceholders.PROTOCOL]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.PROTOCOL]?.toUpperCase()),
            [EventPalceholders.REWARD]: rewardPalceholders.reward,
            [EventPalceholders.STATUS]: notificationInterface.status,
            [EventPalceholders.TARGET_IP]: this.prepareFinalPalceholderForDisplay(notificationInterface.target_ip),
            [EventPalceholders.THREAT_NAME]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.THREAT_NAME]),
            [EventPalceholders.THREAT_TYPE]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventConditions.THREAT_TYPE]),
            [EventPalceholders.THREATS_COUNT]: notificationInterface.threats_count,
            [EventPalceholders.TIMESTAMP] : this.computeTimestampPlaceholder(notification),
            [EventPalceholders.NUMBER_VULNERABILITIES]: notificationInterface.total_vulns,
            [EventPalceholders.URL]: this.prepareFinalPalceholderForDisplay(notificationInterface.url),
            [EventPalceholders.VENDOR]: this.prepareFinalPalceholderForDisplay(notificationInterface.vendor),
            [EventPalceholders.VERSION]: this.prepareFinalPalceholderForDisplay(notificationInterface[EventDescriptors.VERSION])
        };
    }

    /**
     * Computes the notification tips text
     * @param notification The notification object
     * @param notificationInterface The notification interface
     * @param type The notification type
     * @param subtype The notification subtype
     * @returns {string} The notification tips text
     */
    private computeNotificationTips(notification, notificationInterface, type, subtype): string {
        let tips: any = '';
        const errorCode = notification?.app_fields.notification_params?.error_code;
        if (errorCode) {
            tips = this.valuesService.notificationsThreatErrors[errorCode];
        } else {
            tips = this.eventsService.getEventPropertyTexts(EventDescriptors.TIPS, notificationInterface, this.valuesService.notificationModule[type][subtype]);
        }
        if (this.malwareVsNoMalwareTips[notificationInterface.tips] && !notificationInterface?.placeholders?.malware) {
            tips = this.malwareVsNoMalwareTips[notificationInterface.tips];
        }
        return tips;
    }

    /**
     * Computes the text for the no action needed description
     * @param notificationInterface Notification interface
     * @param type Notification type
     * @returns {string} The no action needed description
     */
    private computeNotificationNoActionNeededDesc(notificationInterface, type): string {
        let noActionNeededDesc = '';
        if (!notificationInterface.main_link && !notificationInterface.main_button && !notificationInterface.secondary_button && type !== NotificationsTypes.PARENTAL && type !== NotificationsTypes.NCC) {
            noActionNeededDesc = 'notifications.noActionRequired';
        }
        return noActionNeededDesc;
    }

    /**
     * @description Computes the notification icon for the interface
     * @param type Notification type
     * @param subtype Notification subtype
     * @returns {number|UiIcon.Svg} The icon string or null
     */
    private computeNotificationIcon(type, subtype): number|string {
        const icon = this.valuesService.notificationModule?.[type]?.[subtype]?.[EventDescriptors.ICON];
        if (icon !== undefined) {
            return icon
        }
        return '';
    }

   /**
    * @description Creates the notification interface
    */
    public createNotificationsDetails(): void {
        let notifications = [];
        notifications = this.notificationsService.getNotificationsList();

        for (const notification of notifications) {
            this.createNotificationDetails(notification);
        }
    }

    /**
     * @description Creates the interface for a single notification
     * @param notification the raw notification
     * @returns {void} nothing, appends the interface object and it's calculated properties to the original notification
     */
    public createNotificationDetails(notification): void{

        const notificationInterface: any = {};

        // > add notificaiton params to notification interface
        this.addNotificationDefaultSubtypeToNotification(notification);
        this.addNotificationParamsToNotificationInterface(notification, notificationInterface);
        this.addNotificationDataParamsToNotificationInterface(notification, notificationInterface);
        // > then add everything else
        this.addNotificationInterfaceParamsToInterface(notificationInterface);
        this.addNotificationInterfaceDeviceAndBoxId(notification, notificationInterface);
        this.addNotificationInterfaceAccessedFromApp(notificationInterface);
        this.addNotificationInterfaceThreatsInfo(notificationInterface);
        this.addNotificationInterfaceDeviceAndBoxInfo(notification, notificationInterface);
        this.addNotificationInterfaceProfileInfo(notificationInterface);
        this.addNotificationInterfaceProtocol(notificationInterface);
        this.addNotificationPlaceholdersToInterface(notification, notificationInterface);
        // > then add the text

        const type = notification.app_fields.notification_params.notification_type;
        const subtype = this.getNotificationSubtype(notification);

        // titlul notificarilor afisat este cel venit din connect
        notificationInterface.title = notification.user_fields.text;
        const notificationTemplate = this.valuesService.notificationModule?.[type]?.[subtype];
        notificationInterface.desc_threat = this.eventsService.getEventPropertyTexts(EventDescriptors.DESC_THREAT, notificationInterface, notificationTemplate);
        notificationInterface.desc = this.eventsService.getEventPropertyTexts(EventDescriptors.DESC, notificationInterface, notificationTemplate);
        notificationInterface.desc_title = this.eventsService.getEventPropertyTexts(EventDescriptors.DESC_TITLE, notificationInterface, notificationTemplate);
        notificationInterface.desc_device = this.eventsService.getEventPropertyTexts(EventDescriptors.DESC_DEVICE, notificationInterface, notificationTemplate);
        notificationInterface.tips_title = this.eventsService.getEventPropertyTexts(EventDescriptors.TIPS_TITLE, notificationInterface, notificationTemplate);
        notificationInterface.tips = this.computeNotificationTips(notification, notificationInterface, type, subtype);
        const noAdobeLink = this.eventsService.getEventPropertyTexts(EventDescriptors.LINK, notificationInterface, notificationTemplate);
        notificationInterface.link = this.adobeDataLayerService.addRefAndVisitor(noAdobeLink as string);
        notificationInterface.main_button = notificationTemplate?.[EventDescriptors.MAIN_BUTTON]?.[EventDescriptors.TEXT];
        notificationInterface.main_button_disabled = this.getMainButtonDisabledState(notificationInterface, type, subtype);
        notificationInterface.main_button_funct = notificationTemplate?.[EventDescriptors.MAIN_BUTTON]?.[EventDescriptors.FUNCTION];
        notificationInterface.main_button_desc = notificationTemplate?.[EventDescriptors.MAIN_BUTTON]?.[EventDescriptors.DESC]
                                                ?? this.valuesService.notificationsKeys.empty;
        notificationInterface.secondary_button = this.eventsService.getEventPropertyTexts(EventDescriptors.SECONDARY_BUTTON, notificationInterface, notificationTemplate);
        notificationInterface.secondary_button_disabled = this.getSecondaryButtonDisabledState(notificationInterface);
        notificationInterface.secondary_button_funct = notificationTemplate?.[EventDescriptors.SECONDARY_BUTTON]?.[EventDescriptors.FUNCTION];
        notificationInterface.secondary_button_desc = notificationTemplate?.[EventDescriptors.SECONDARY_BUTTON]?.[EventDescriptors.DESC]
                                                        ?? this.valuesService.notificationsKeys.empty;
        notificationInterface.main_link = notificationTemplate?.[EventDescriptors.MAIN_LINK]?.text;
        notificationInterface.main_link_funct = notificationTemplate?.[EventDescriptors.MAIN_LINK]?.[EventDescriptors.FUNCTION];
        notificationInterface.main_link_desc = notificationTemplate?.[EventDescriptors.MAIN_LINK]?.[EventDescriptors.DESC]
                                                ?? this.valuesService.notificationsKeys.empty;
        notificationInterface.device_status = this.eventsService.getEventDeviceStatusInfo(notificationTemplate);
        notificationInterface.noActionNeededDesc = this.computeNotificationNoActionNeededDesc(notificationInterface, type);
        notificationInterface.icon = this.computeNotificationIcon(type, subtype);

        const exception = this.devicesService.getException(notificationInterface?.threat_id);
        if (exception) {
            this.changeNotificationInterfaceMainLinkInfoForException(notificationInterface);
        }
        notification.interface = notificationInterface;
    }
}
